import {
    MDBBtn,
    MDBInput,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle
} from 'mdb-react-ui-kit';
import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from 'react';
import {AppContext} from '../App';
import UserService from '../../services/user-service';
import {Impressum, PrivacyStatement} from './SiteInfo';
import "./LoginView.css";
import {DefaultEventService, GlyphEventService, TaskEditorEventService} from '../../services/event-service';
import {User} from 'konect-model';
import {Settings} from '../Settings';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface LoginViewProps {
    legalState: [string, Dispatch<SetStateAction<string>>];
}

enum LOGIN_STATE {
    INITIAL = 0,
    JOINING,
    FAILED,
    USEREXISTS,
    MISMATCH,
    FORGOT_PASSWORD,
    RESET_PASSWORD,
    RESET_SUCCESS,
    RESET_REQUEST_SENT,
    RESET_INVALID_TOKEN
}

const LoginView = (props: LoginViewProps) => {

    const {setUser} = useContext(AppContext);

    const [login, setLogin] = useState<boolean>(true);
    const [username, setUsername] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [loginState, setLoginState] = useState<LOGIN_STATE>(LOGIN_STATE.INITIAL);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Password reset state
    const [resetEmail, setResetEmail] = useState<string>("");
    const [resetToken, setResetToken] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");


    useEffect(() => {
        //resets all event services once the main application view is closed
        DefaultEventService.reset();
        TaskEditorEventService.reset();
        GlyphEventService.reset();
    }, [])

    // If already authenticated (e.g., valid refresh token), redirect to home
    useEffect(() => {
        let mounted = true;
        const ensureAuthenticated = async () => {
            try {
                const authenticatedUser = await UserService.initUserState();
                if (mounted && authenticatedUser && authenticatedUser.username) {
                    setUser(authenticatedUser);
                    navigate('/', { replace: true });
                }
            } catch (e) {
                // ignore: stay on login
            }
        };
        ensureAuthenticated();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check URL for password reset params
    useEffect(() => {
        const token = searchParams.get('resetToken');
        const email = searchParams.get('resetEmail');
        if (token && email) {
            setResetToken(token);
            setResetEmail(email);
            validateAndShowResetForm(token, email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const validateAndShowResetForm = async (token: string, email: string) => {
        setLoginState(LOGIN_STATE.JOINING);
        const result = await UserService.validateResetToken(email, token);
        if (result) {
            setLoginState(LOGIN_STATE.RESET_PASSWORD);
        } else {
            setLoginState(LOGIN_STATE.RESET_INVALID_TOKEN);
        }
    };

    useEffect(() => {
        setLoginState(LOGIN_STATE.INITIAL);
    }, [login])

    const [legalState, setLegalState] = props.legalState;

    const loginUser = async () => {
        if (username && password) {
            Settings.setUseOfflineMode(false);
            setLoginState(LOGIN_STATE.JOINING);
            const response = await UserService.loginUser(username, password)
            if (response) {
                setUser(response)
                // ensure any legal modal is closed to avoid leftover backdrops
                setLegalState("");
                navigate('/');
                return;
            }
            setLoginState(LOGIN_STATE.FAILED);
        }
    }

    const registerUser = async () => {
        if (username && password && confirmPassword === password) {
            Settings.setUseOfflineMode(false);
            setLoginState(LOGIN_STATE.JOINING);
            // Pass all user data to the createUser service
            const response = await UserService.createUser(username, password, firstName, lastName, email);
            if (response) {
                // On success: set user and go to home
                setUser(response);
                setLegalState("");
                // In case any modal artifacts remain, ensure clean state before navigation
                // @ts-ignore - local helper declared above
                typeof cleanupModals === 'function' && cleanupModals();
                navigate('/', { replace: true });
                return;
            }
            // If creation failed (e.g., username exists), show appropriate error state
            setLoginState(LOGIN_STATE.USEREXISTS);
        }
    }

    const getLoginStateClass = () => {
        switch (loginState) {
            case LOGIN_STATE.FAILED:
                return " error";
            case LOGIN_STATE.USEREXISTS:
                return " user-exists";
            case LOGIN_STATE.MISMATCH:
                return " password-missmatch";
            default:
                return ""
        }
    }

    const getSubmitButtonStyle = () => {
        if (loginState === LOGIN_STATE.FAILED || loginState === LOGIN_STATE.USEREXISTS) {
            return {animation: "shake 0.25s ease-in-out forwards"}
        }
        return {}
    }

    const updatePassword = (e: any) => {
        setPassword(e.target.value);
        if (e.target.value !== confirmPassword && confirmPassword !== "") {
            setLoginState(LOGIN_STATE.MISMATCH);
            return;
        }
        setLoginState(LOGIN_STATE.INITIAL);
    }

    const updatePasswordConfirm = (e: any) => {
        setConfirmPassword(e.target.value);
        if (e.target.value !== password) {
            setLoginState(LOGIN_STATE.MISMATCH);
            return;
        }
        setLoginState(LOGIN_STATE.INITIAL)
    }

    const confirm = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            login ? loginUser() : registerUser()
        }
    }

    const loginAsGuest = () => {
        // Enable offline mode and set a guest user without any server calls
        Settings.setUseOfflineMode(true);
        // Clear any possible remnants from previous sessions
        localStorage.removeItem('accessToken');
        // Initialize guest user and persist it for reloads
        const guest = new User();
        guest.username = "Guest";
        localStorage.setItem('user', JSON.stringify(guest));
        setUser(guest);
        // ensure any legal modal is closed to avoid leftover backdrops
        setLegalState("");
        // Navigate to home using SPA navigation to avoid full reload
        navigate('/', { replace: true });
    }

    // Password reset handlers
    const handleRequestReset = async () => {
        if (!resetEmail) return;
        setLoginState(LOGIN_STATE.JOINING);
        const result = await UserService.requestPasswordReset(resetEmail);
        if (result) {
            setLoginState(LOGIN_STATE.RESET_REQUEST_SENT);
        } else {
            setLoginState(LOGIN_STATE.FAILED);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || !confirmNewPassword || newPassword !== confirmNewPassword) {
            setLoginState(LOGIN_STATE.MISMATCH);
            return;
        }
        if (newPassword.length < 6) {
            setLoginState(LOGIN_STATE.FAILED);
            return;
        }
        setLoginState(LOGIN_STATE.JOINING);
        const result = await UserService.resetPassword(resetEmail, resetToken, newPassword);
        if (result) {
            // Clear URL params
            setSearchParams({});
            setLoginState(LOGIN_STATE.RESET_SUCCESS);
            // Auto-redirect to login after 3 seconds
            setTimeout(() => {
                setLoginState(LOGIN_STATE.INITIAL);
                setNewPassword("");
                setConfirmNewPassword("");
                setResetEmail("");
                setResetToken("");
            }, 3000);
        } else {
            setLoginState(LOGIN_STATE.RESET_INVALID_TOKEN);
        }
    };

    const backToLogin = () => {
        setSearchParams({});
        setLoginState(LOGIN_STATE.INITIAL);
        setResetEmail("");
        setResetToken("");
        setNewPassword("");
        setConfirmNewPassword("");
    };

    // Render password reset views
    const renderPasswordResetViews = () => {
        switch (loginState) {
            case LOGIN_STATE.FORGOT_PASSWORD:
                return (
                    <div>
                        <h5 className='mb-3 text-center'>Reset Password</h5>
                        <p className='mb-3 text-center' style={{fontSize: '0.9em'}}>
                            Enter your email address and we'll send you a link to reset your password.
                        </p>
                        <MDBInput className='mb-4' type='email' label='Email' autoComplete='email' value={resetEmail}
                                  onChange={(e: any) => setResetEmail(e.target.value)}/>
                        <MDBBtn disabled={!resetEmail} onClick={handleRequestReset}
                                className='mb-4 login-button' block>
                            Send Reset Link
                        </MDBBtn>
                        <div className='text-center'>
                            <a href="#" onClick={backToLogin}>Back to Login</a>
                        </div>
                    </div>
                );
            case LOGIN_STATE.RESET_REQUEST_SENT:
                return (
                    <div className='text-center'>
                        <h5 className='mb-3'>Check Your Email</h5>
                        <p className='mb-4'>
                            If an account with that email exists, we've sent a password reset link.
                            Please check your inbox and spam folder.
                        </p>
                        <MDBBtn onClick={backToLogin} className='login-button' block>
                            Back to Login
                        </MDBBtn>
                    </div>
                );
            case LOGIN_STATE.RESET_PASSWORD:
                const passwordsMatch = newPassword === confirmNewPassword;
                const showMismatchWarning = confirmNewPassword !== "" && !passwordsMatch;
                return (
                    <div>
                        <h5 className='mb-3 text-center'>Set New Password</h5>
                        <MDBInput className='mb-2' type='password' label='New Password' autoComplete='new-password'
                                  value={newPassword}
                                  onChange={(e: any) => setNewPassword(e.target.value)}/>
                        <MDBInput className='mb-2' type='password' label='Confirm New Password' autoComplete='new-password'
                                  value={confirmNewPassword}
                                  onChange={(e: any) => setConfirmNewPassword(e.target.value)}/>
                        {newPassword && newPassword.length < 6 && (
                            <p className='text-danger mb-2' style={{fontSize: '0.8em'}}>
                                Password must be at least 6 characters
                            </p>
                        )}
                        {showMismatchWarning && (
                            <p className='text-danger mb-2' style={{fontSize: '0.8em'}}>
                                Passwords do not match
                            </p>
                        )}
                        <MDBBtn disabled={!newPassword || !confirmNewPassword || !passwordsMatch || newPassword.length < 6}
                                onClick={handleResetPassword}
                                className='mb-4 login-button' block>
                            Reset Password
                        </MDBBtn>
                        <div className='text-center'>
                            <a href="#" onClick={backToLogin}>Back to Login</a>
                        </div>
                    </div>
                );
            case LOGIN_STATE.RESET_SUCCESS:
                return (
                    <div className='text-center'>
                        <h5 className='mb-3' style={{color: 'green'}}>Password Reset Successful!</h5>
                        <p className='mb-4'>
                            Your password has been reset. You will be redirected to the login page shortly.
                        </p>
                        <MDBBtn onClick={backToLogin} className='login-button' block>
                            Go to Login
                        </MDBBtn>
                    </div>
                );
            case LOGIN_STATE.RESET_INVALID_TOKEN:
                return (
                    <div className='text-center'>
                        <h5 className='mb-3' style={{color: 'red'}}>Invalid or Expired Link</h5>
                        <p className='mb-4'>
                            This password reset link is invalid or has expired.
                            Please request a new one.
                        </p>
                        <MDBBtn onClick={() => setLoginState(LOGIN_STATE.FORGOT_PASSWORD)} className='mb-2 login-button' block>
                            Request New Link
                        </MDBBtn>
                        <MDBBtn onClick={backToLogin} className='login-button guest' block>
                            Back to Login
                        </MDBBtn>
                    </div>
                );
            default:
                return null;
        }
    };

    // Check if we're in a password reset state
    const isPasswordResetState = [
        LOGIN_STATE.FORGOT_PASSWORD,
        LOGIN_STATE.RESET_PASSWORD,
        LOGIN_STATE.RESET_SUCCESS,
        LOGIN_STATE.RESET_REQUEST_SENT,
        LOGIN_STATE.RESET_INVALID_TOKEN
    ].includes(loginState);

    return (
        <div className={'d-flex align-items-center justify-content-center w-100 login-screen' + (getLoginStateClass())}
             onKeyDown={confirm}>
            {
                isPasswordResetState ? renderPasswordResetViews() :
                login ?
                    <div>
                        <MDBInput className='mb-2' type='text' label='Username' autoComplete='username' value={username} onChange={(e: any) => {
                            setUsername(e.target.value);
                            setLoginState(LOGIN_STATE.INITIAL)
                        }}/>
                        <MDBInput className='mb-4' type='password' label='Password' autoComplete='current-password' value={password}
                                  onChange={(e: any) => {
                                      setPassword(e.target.value);
                                      setLoginState(LOGIN_STATE.INITIAL)
                                  }}/>

                        <MDBBtn disabled={!username || !password} onClick={() => loginUser()}
                                className='mb-4 login-button' style={getSubmitButtonStyle()} block>
                            {loginState !== LOGIN_STATE.JOINING ? "Login" :
                                <i className={"fas fa-spinner accept-modal-spinner"} style={{fontSize: "1.5em"}}/>}
                        </MDBBtn>
                        <div className='text-center'>
                            <p>
                                Not a member? <a href="#" onClick={() => setLogin(false)}>Register</a>
                            </p>
                            <p>
                                <a href="#" onClick={() => setLoginState(LOGIN_STATE.FORGOT_PASSWORD)}>Forgot password?</a>
                            </p>
                        </div>
                        <MDBBtn onClick={() => loginAsGuest()} className='mt-4 login-button guest' block>
                            Login As Guest
                        </MDBBtn>
                    </div> :
                    <div>
                        <MDBInput className='mb-2' type='text' label='Username' autoComplete='username' value={username} onChange={(e: any) => {
                            setUsername(e.target.value);
                            setLoginState(LOGIN_STATE.INITIAL)
                        }}/>
                        <MDBInput className='mb-2' type='text' label='E-Mail' autoComplete='email' value={email} onChange={(e: any) => {
                            setEmail(e.target.value);
                            setLoginState(LOGIN_STATE.INITIAL)
                        }}/>
                        <MDBInput className='mb-2' type='text' label='First Name' autoComplete='given-name' value={firstName} onChange={(e: any) => {
                            setFirstName(e.target.value);
                            setLoginState(LOGIN_STATE.INITIAL)
                        }}/>
                        <MDBInput className='mb-2' type='text' label='Last Name' autoComplete='family-name' value={lastName} onChange={(e: any) => {
                            setLastName(e.target.value);
                            setLoginState(LOGIN_STATE.INITIAL)
                        }}/>
                        <MDBInput className='mb-2' type='password' label='Password' autoComplete='new-password' value={password}
                                  onChange={updatePassword}/>
                        <MDBInput className='mb-4' type='password' label='Confirm Password' autoComplete='new-password' value={confirmPassword}
                                  onChange={updatePasswordConfirm}/>
                        <MDBBtn disabled={!username || !password || confirmPassword !== password}
                                onClick={() => registerUser()} className='mb-4 login-button'
                                style={getSubmitButtonStyle()}
                                block>
                            Register
                        </MDBBtn>

                        <div className='text-center'>
                            <p>
                                Already a member? <a href="#" onClick={() => setLogin(true)}>Login</a>
                            </p>
                        </div>
                    </div>
            }
            <MDBModal closeOnEsc show={!!legalState}>
                <MDBModalDialog style={{maxHeight: "100vh"}} size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{legalState === "legalNotice" ? <>Legal Notice</> : <>Privacy
                                Statement</>}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setLegalState("")}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {legalState === "legalNotice" && <Impressum/>}
                            {legalState === "privacyStatement" && <PrivacyStatement/>}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </div>
    );
};

export default LoginView;
